import { useSyncExternalStore } from 'react';

const NARROW = '(max-width: 760px)';
/** A phone, or a tablet held in the hand: a touch screen up to a large tablet's width. */
const FIELD = '(max-width: 760px), (pointer: coarse) and (max-width: 1366px)';

/** The media query, where the environment has one (not in some test environments). */
const media = (query: string): MediaQueryList | null =>
  typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;

function useQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = media(query);
      list?.addEventListener('change', onChange);
      return () => {
        list?.removeEventListener('change', onChange);
      };
    },
    () => media(query)?.matches ?? false,
    () => false,
  );
}

/** True on phone-sized screens, where the master data uses a list and forms instead of a grid. */
export function useNarrowScreen(): boolean {
  return useQuery(NARROW);
}

/** A touch screen no larger than a tablet (some browsers report no coarse pointer for it). */
function touchTablet(): boolean {
  return (
    navigator.maxTouchPoints > 0 &&
    Math.min(window.screen.width, window.screen.height) <= 1024 &&
    window.innerWidth <= 1366
  );
}

/**
 * True where the field layout fits: phones and touch tablets (the handover fills the screen, one
 * room at a time, with large targets), instead of the long desktop editor.
 */
export function useFieldMode(): boolean {
  const field = useQuery(FIELD);
  const narrow = useNarrowScreen();
  return field || narrow || (typeof navigator !== 'undefined' && touchTablet());
}
